import { useState, useCallback, useEffect } from 'react'
import { Modal, Button, Checkbox, Select, message } from '@/components/ui'
import DynamicFormField from './DynamicFormField'
import type { RegistrationField } from '@/pages/events/attendees/types'

interface BulkUpdateModalProps {
  open: boolean
  onClose: () => void
  selectedCount: number
  selectedIds: (string | number)[]
  registrationFields: RegistrationField[]
  eventSlug: string
  onSubmit: (updates: BulkUpdatePayload) => Promise<void>
}

export interface BulkUpdatePayload {
  attendee_ids: number[]
  updates: Record<string, any>
}

interface FieldState {
  enabled: boolean
  value: any
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
]

// Status transition rules (bulk update):
// - pending → approved, rejected, or cancelled
// - approved → pending or cancelled
// - rejected → approved or cancelled
// - cancelled → approved

export default function BulkUpdateModal({
  open,
  onClose,
  selectedCount,
  selectedIds,
  registrationFields,
  eventSlug,
  onSubmit,
}: BulkUpdateModalProps) {
  // State for each field: { enabled: boolean, value: any }
  const [fieldStates, setFieldStates] = useState<Record<string, FieldState>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Initialize/reset state when modal opens
  const resetState = useCallback(() => {
    const initialStates: Record<string, FieldState> = {
      status: { enabled: false, value: undefined },
    }
    registrationFields.forEach((field) => {
      // Skip file_upload fields - can't bulk update files
      if (field.kind !== 'file_upload') {
        initialStates[field.identifier] = { enabled: false, value: undefined }
      }
    })
    setFieldStates(initialStates)
  }, [registrationFields])

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      resetState()
    }
  }, [open, resetState])

  const toggleField = (identifier: string) => {
    setFieldStates((prev) => ({
      ...prev,
      [identifier]: {
        ...prev[identifier],
        enabled: !prev[identifier]?.enabled,
      },
    }))
  }

  const updateFieldValue = (identifier: string, value: any) => {
    setFieldStates((prev) => ({
      ...prev,
      [identifier]: {
        ...prev[identifier],
        value,
      },
    }))
  }

  const handleSubmit = async () => {
    // Collect only enabled fields with values
    const updates: Record<string, any> = {}
    let hasUpdates = false

    Object.entries(fieldStates).forEach(([identifier, state]) => {
      if (state.enabled && state.value !== undefined && state.value !== '') {
        updates[identifier] = state.value
        hasUpdates = true
      }
    })

    if (!hasUpdates) {
      message.warning('Please enable and set at least one field to update')
      return
    }

    setIsSubmitting(true)

    try {
      await onSubmit({
        attendee_ids: selectedIds.map((id) => Number(id)),
        updates,
      })
      // Success message and modal close are handled by the parent
    } catch (error: any) {
      // Error message is also shown by parent, but we keep isSubmitting false
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      resetState()
      onClose()
    }
  }

  // Filter out file_upload fields
  const updateableFields = registrationFields.filter((f) => f.kind !== 'file_upload')

  const enabledFieldCount = Object.values(fieldStates).filter((s) => s.enabled).length

  return (
    <Modal
      title={`Update ${selectedCount} attendee${selectedCount !== 1 ? 's' : ''}`}
      open={open}
      onClose={handleClose}
      width={600}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="tertiary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || enabledFieldCount === 0}
          >
            {isSubmitting ? 'Updating...' : `Update ${selectedCount} Attendee${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </div>
      }
    >
      <div className="space-y-1">
        <p className="text-sm text-gray-500 mb-4">
          Check the fields you want to update. Only checked fields will be modified.
        </p>

        {/* Status Field */}
        <div className="border border-gray-200 rounded-lg p-3">
          <div className="flex items-start gap-3">
            <Checkbox
              checked={fieldStates.status?.enabled || false}
              onChange={() => toggleField('status')}
              className="mt-1"
            />
            <div className="flex-1">
              <label className="block text-sm font-medium mb-1">Status</label>
              <Select
                className="w-full"
                placeholder="Select status"
                value={fieldStates.status?.value}
                onChange={(value) => updateFieldValue('status', value)}
                options={STATUS_OPTIONS}
                disabled={!fieldStates.status?.enabled}
                size="small"
              />
              {fieldStates.status?.enabled && fieldStates.status?.value && (
                <p className="text-xs text-gray-500 mt-2">
                  {fieldStates.status.value === 'rejected' && (
                    <span className="text-orange-600">
                      Note: Only pending registrations will be rejected.
                    </span>
                  )}
                  {fieldStates.status.value === 'approved' && (
                    <span className="text-green-600">
                      Note: Only non-approved registrations will be approved. Approved registrations will be skipped.
                    </span>
                  )}
                  {fieldStates.status.value === 'cancelled' && (
                    <span className="text-red-600">
                      Note: Registrations that are already cancelled will be skipped.
                    </span>
                  )}
                  {fieldStates.status.value === 'pending' && (
                    <span className="text-gray-600">
                      Note: Only approved registrations can be changed back to pending.
                    </span>
                  )}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Dynamic Registration Fields */}
        <div className="max-h-[400px] overflow-y-auto space-y-1">
          {updateableFields.map((field) => (
            <div key={field.identifier} className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={fieldStates[field.identifier]?.enabled || false}
                  onChange={() => toggleField(field.identifier)}
                  className="mt-1"
                />
                <div className="flex-1">
                  {fieldStates[field.identifier]?.enabled ? (
                    <DynamicFormField
                      field={field}
                      value={fieldStates[field.identifier]?.value}
                      onChange={(_, value) => updateFieldValue(field.identifier, value)}
                      eventSlug={eventSlug}
                    />
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-400">
                        {field.label}
                        {field.required && <span className="text-red-300 ml-1">*</span>}
                      </label>
                      <p className="text-xs text-gray-400 mt-1">
                        Check to enable this field for bulk update
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {enabledFieldCount === 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-yellow-800">
              No fields selected. Check at least one field to update.
            </p>
          </div>
        )}

        {enabledFieldCount > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
            <p className="text-sm text-blue-800">
              <strong>{enabledFieldCount}</strong> field{enabledFieldCount !== 1 ? 's' : ''} will be updated for{' '}
              <strong>{selectedCount}</strong> attendee{selectedCount !== 1 ? 's' : ''}.
            </p>
          </div>
        )}
      </div>
    </Modal>
  )
}
